import React, { useState, useEffect, useCallback, memo } from "react";
import SectionWrapper from "../components/SectionWrapper";
import KeyStrengths from "../components/KeyStrengths";
const PackageCard = memo(({ pkg }) => (
  <div key={pkg.name} className="border rounded-xl shadow-lg p-6 bg-maroon">
    <h3 className="text-2xl font-semibold text-white mb-2">{pkg.name}</h3>
    <p className="text-xl font-bold mb-4 text-gold">{pkg.price}</p>
    <ul className="space-y-2 text-white ">
      {Object.entries(pkg.features).map(([feature, value]) => (
        <li
          key={feature}
          className="flex items-center justify-between border-b py-2"
        >
          <span>{feature}</span>
          <span>
            {typeof value === "boolean" ? (value ? "✅" : "❌") : value}
          </span>
        </li>
      ))}
    </ul>
  </div>
));

const ProcessStep = memo(({ step }) => (
  <div
    key={step.step}
    className="border-l-4 border-maroon pl-4 py-4 bg-white rounded-lg shadow-md"
  >
    <h3 className="text-xl font-semibold text-maroon mb-2">
      Step {step.step}: {step.title}
    </h3>
    <p className="text-gray-700">{step.description}</p>
  </div>
));

const ServicesPage = () => {
  const [packages, setPackages] = useState([]);
  const [processSteps, setProcessSteps] = useState([]);

  // ✅ useCallback to memoize fetch functions (prevents re-creation)
  const fetchPackages = useCallback(async () => {
    try {
      const res = await fetch("/AllJson/annualPackages_10.json", {
        cache: "force-cache",
      });
      const data = await res.json();
      setPackages(data.annualPackages || []);
    } catch (err) {
      console.error("Failed to load packages:", err);
    }
  }, []);

  const fetchProcess = useCallback(async () => {
    try {
      const res = await fetch("/AllJson/ourprocess_10.json", {
        cache: "force-cache",
        headers: { "Cache-Control": "public, max-age=86400" },
      });
      const data = await res.json();
      setProcessSteps(data.process || []);
    } catch (err) {
      console.error("Failed to load process steps:", err);
    }
  }, []);

  // ✅ Only run once
  useEffect(() => {
    fetchPackages();
    fetchProcess();
  }, [fetchPackages, fetchProcess]);

  return (
    <div className="my-16 w-full text-maroon">
      <SectionWrapper className="pt-10">
        <KeyStrengths />
        {/* ✅ Process Section */}
        <div className="relative py-10 px-4 sm:px-6 md:px-10">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-maroon text-center mb-10 md:mb-16">
            Our Process
          </h3>

          <div className="relative max-w-5xl mx-auto">
            {/* Vertical timeline line */}
            <div className="absolute left-1/2 top-0 h-full w-[2px] bg-gray-300 transform -translate-x-1/2 hidden md:block"></div>

            {processSteps.length === 0 ? (
              <p className="text-center text-gray-500">
                Loading process steps...
              </p>
            ) : (
              processSteps.map((step, index) => (
                <div
                  key={step.step}
                  className={`relative mb-12 flex flex-col md:flex-row items-center ${
                    index % 2 === 0 ? "md:justify-start" : "md:justify-end"
                  }`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 bg-maroon w-5 h-5 rounded-full border-4 border-white shadow-md hidden md:block"></div>

                  <div
                    className={`w-full md:w-1/2 ${
                      index % 2 === 0
                        ? "md:pr-12 text-center md:text-left"
                        : "md:pl-12 text-center md:text-left"
                    }`}
                  >
                    <div className="bg-white shadow-lg rounded-2xl p-5 sm:p-6 transition-transform duration-300 hover:scale-[1.03]">
                      <h4 className="text-lg sm:text-xl font-semibold text-maroon mb-2 uppercase lg:hidden">
                        Step {index + 1}
                      </h4>
                      <h4 className="text-lg sm:text-xl font-semibold text-maroon mb-2 uppercase">
                        {step.title}
                      </h4>
                      <p
                        className="text-gray-700 text-sm sm:text-base leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: step.description }}
                      ></p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
};

export default memo(ServicesPage);
